import { Box, Button, Group, Paper, Portal, Text, Title } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";

export interface TourStep {
    target: string;
    title: string;
    description: string;
    position?: "top" | "right" | "bottom" | "left";
}

interface GuidedTourProps {
    steps: TourStep[];
    onComplete: () => void;
    isOpen: boolean;
    onClose: () => void;
    pathCheck: () => boolean;
}

const pulse = keyframes`
    0% { box-shadow: 0 0 0 0 rgba(100, 30, 140, 0.4); }
    70% { box-shadow: 0 0 0 10px rgba(100, 30, 140, 0); }
    100% { box-shadow: 0 0 0 0 rgba(100, 30, 140, 0); }
`;

const PulsedBox = styled.div<{
    top: number;
    left: number;
    width: number;
    height: number;
}>`
    position: fixed;
    border: 2px solid #641E8C;
    border-radius: 4px;
    z-index: 999;
    animation: ${pulse} 2s infinite;
    transition: all 0.3s ease;
    background-color: rgba(100, 30, 140, 0.05);

    /* Dynamic styles via props */
    top: ${(props) => props.top}px;
    left: ${(props) => props.left}px;
    width: ${(props) => props.width}px;
    height: ${(props) => props.height}px;
`;

// Add a fade transition for smooth appearance/disappearance
// TODO(AI): Fix this
// Note(AI): Usage is commented below
const _fadeIn = keyframes`
    from: { opacity: 0 },
    to: { opacity: 1 },
`;

export function GuidedTour({ steps, onComplete, isOpen, onClose, pathCheck }: GuidedTourProps) {
    const [currentStep, setCurrentStep] = useState(0);
    const [targetElement, setTargetElement] = useState<DOMRect | null>(null);
    const [previousTarget, setPreviousTarget] = useState<Element | null>(null);
    const [isTargetFound, setIsTargetFound] = useState(false);
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);

    // Replace state with ref for retries
    const retriesRef = useRef(0);

    // Check if we're on the correct page
    const inTourPage = useRef(false);

    // Determine if we're on the correct page when the component mounts or updates
    useEffect(() => {
        inTourPage.current = pathCheck();
        if (!inTourPage.current && isOpen) {
            // If not on correct page but tour is open, complete the tour
            onComplete();
        }
    }, [isOpen, onComplete, pathCheck]);

    useEffect(() => {
        if (!isOpen) return;

        // Reset target found state when changing steps
        setIsTargetFound(false);
        setIsTooltipVisible(false);

        // Clean up any previously highlighted elements from the last step
        if (previousTarget) {
            previousTarget.removeAttribute("data-tour-highlight");
            (previousTarget as HTMLElement).style.pointerEvents = "";
        }

        // Reset retries on step change
        retriesRef.current = 0;

        const findAndHighlightTarget = () => {
            // Slight delay to ensure DOM is ready
            const timeoutId = setTimeout(() => {
                const targetSelector = steps[currentStep].target;

                // Try to find the target element
                const target = document.querySelector(targetSelector);

                if (target) {
                    const rect = target.getBoundingClientRect();
                    setTargetElement(rect);
                    setPreviousTarget(target);

                    // Add highlight effect to the target element and increase z-index
                    target.setAttribute("data-tour-highlight", "true");

                    // Make sure the element is visible - scroll to it if needed
                    const viewportHeight = window.innerHeight;
                    if (rect.top < 0 || rect.bottom > viewportHeight) {
                        target.scrollIntoView({ behavior: "smooth", block: "center" });
                    }

                    // Make sure the target element is clickable
                    (target as HTMLElement).style.pointerEvents = "auto";

                    // First mark the target as found (keeps overlay visible)
                    setIsTargetFound(true);

                    // Then show the tooltip with a slight delay for smooth transition
                    setTimeout(() => {
                        setIsTooltipVisible(true);
                    }, 50);
                } else if (retriesRef.current < 5) {
                    // If target not found, try a more generic selector for Mantine components

                    // For datasource selector specifically, try to find it by other means if needed
                    if (targetSelector.includes("datasource-selector") && retriesRef.current >= 2) {
                        // Try more generic selector that might catch Mantine's structure
                        const alternativeTarget = document.querySelector(
                            '.mantine-Select-root[data-tour="datasource-selector"]',
                        );
                        if (alternativeTarget) {
                            const rect = alternativeTarget.getBoundingClientRect();
                            setTargetElement(rect);
                            setPreviousTarget(alternativeTarget);
                            alternativeTarget.setAttribute("data-tour-highlight", "true");

                            const viewportHeight = window.innerHeight;
                            if (rect.top < 0 || rect.bottom > viewportHeight) {
                                alternativeTarget.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                });
                            }

                            (alternativeTarget as HTMLElement).style.pointerEvents = "auto";

                            // First mark the target as found (keeps overlay visible)
                            setIsTargetFound(true);

                            // Then show the tooltip with a slight delay for smooth transition
                            setTimeout(() => {
                                setIsTooltipVisible(true);
                            }, 50);

                            return;
                        }
                    }

                    // Increase retries and try again with a longer delay
                    retriesRef.current += 1;
                    setTimeout(findAndHighlightTarget, 500 * retriesRef.current);
                } else {
                    // Skip to next step if there is one, otherwise close the tour
                    if (currentStep < steps.length - 1) {
                        setCurrentStep(currentStep + 1);
                    } else {
                        onClose();
                    }
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        };

        const cleanup = findAndHighlightTarget();

        return () => {
            cleanup();
            // Clean up highlight effect on all elements
            const highlightedElements = document.querySelectorAll('[data-tour-highlight="true"]');
            // Convert NodeList to Array to make it iterable with for...of
            const elementsArray = Array.from(highlightedElements);
            for (const el of elementsArray) {
                el.removeAttribute("data-tour-highlight");
                (el as HTMLElement).style.pointerEvents = "";
            }
        };
    }, [currentStep, isOpen, steps, previousTarget, onClose]);

    // When the tour closes, ensure all highlighted elements are cleaned up
    useEffect(() => {
        if (!isOpen) {
            const highlightedElements = document.querySelectorAll('[data-tour-highlight="true"]');
            // Convert NodeList to Array to make it iterable with for...of
            const elementsArray = Array.from(highlightedElements);
            for (const el of elementsArray) {
                el.removeAttribute("data-tour-highlight");
                (el as HTMLElement).style.pointerEvents = "";
            }
        }
    }, [isOpen]);

    const handleNext = () => {
        // Hide tooltip immediately during transition, but keep overlay
        setIsTooltipVisible(false);

        if (currentStep < steps.length - 1) {
            // Scroll to top of page before advancing to elements that might be at the top
            if (
                steps[currentStep + 1].target === "#chat-input" ||
                steps[currentStep + 1].target === 'button[data-testid="send-button"]'
            ) {
                // These elements might be at the bottom of the page, so scroll there
                window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
            }

            // Small delay to allow scrolling to complete
            setTimeout(() => {
                setCurrentStep((prev) => prev + 1);
            }, 300);
        } else {
            // Last step, complete the tour
            onComplete();
        }
    };

    const handleSkip = () => {
        // Complete the tour
        onComplete();
    };

    // Don't render anything if tour is closed or not on the correct page
    if (!isOpen || !inTourPage.current) return null;

    const step = steps[currentStep];

    // Calculate tooltip position based on the target element
    const getTooltipPosition = () => {
        if (!targetElement) return { top: 0, left: 0 };

        const position = step.position || "bottom";
        const padding = 10;
        const tooltipWidth = 300;
        const tooltipHeight = 200; // Approximate height of tooltip

        // Get viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let top = 0;
        let left = 0;

        // Calculate initial position based on specified position
        switch (position) {
            case "top":
                top = targetElement.top - tooltipHeight - padding;
                left = targetElement.left + targetElement.width / 2 - tooltipWidth / 2;
                break;
            case "right":
                top = targetElement.top + targetElement.height / 2 - tooltipHeight / 2;
                left = targetElement.right + padding;
                break;
            case "left":
                top = targetElement.top + targetElement.height / 2 - tooltipHeight / 2;
                left = targetElement.left - tooltipWidth - padding;
                break;
            default: // bottom
                top = targetElement.bottom + padding;
                left = targetElement.left + targetElement.width / 2 - tooltipWidth / 2;
        }

        // Adjust if tooltip would go off-screen
        if (left < padding) {
            left = padding;
        } else if (left + tooltipWidth > viewportWidth - padding) {
            left = viewportWidth - tooltipWidth - padding;
        }

        if (top < padding) {
            // If tooltip would go off the top, place it below the element instead
            top = targetElement.bottom + padding;
        } else if (top + tooltipHeight > viewportHeight - padding) {
            // If tooltip would go off the bottom, place it above the element
            top = targetElement.top - tooltipHeight - padding;

            // If still off-screen, position at the bottom with some padding
            if (top < padding) {
                top = viewportHeight - tooltipHeight - padding * 2;
            }
        }

        // Ensure the element is visible in the viewport
        const scrollOptions = {
            behavior: "smooth" as ScrollBehavior,
            block: "center" as ScrollLogicalPosition,
        };

        // If the element is not fully visible, scroll to it
        if (targetElement.top < 0 || targetElement.bottom > viewportHeight) {
            setTimeout(() => {
                const el = document.querySelector(steps[currentStep].target);
                if (el) el.scrollIntoView(scrollOptions);
            }, 300);
        }

        return { top, left };
    };

    const tooltipPosition = getTooltipPosition();

    return (
        <Portal>
            {/* Overlay - always visible when target is found */}
            {isTargetFound && targetElement && (
                <>
                    {/* Top overlay */}
                    <Box
                        style={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: Math.max(0, targetElement.top),
                            background: "rgba(0, 0, 0, 0.5)",
                            zIndex: 999,
                            transition: "all 0.3s ease",
                            // animation: `${fadeIn} 0.3s ease`,
                        }}
                        onClick={handleSkip}
                    />
                    {/* Left overlay */}
                    <Box
                        style={{
                            position: "fixed",
                            top: targetElement.top,
                            left: 0,
                            width: targetElement.left,
                            height: targetElement.height,
                            background: "rgba(0, 0, 0, 0.5)",
                            zIndex: 999,
                            transition: "all 0.3s ease",
                            // animation: `${fadeIn} 0.3s ease`,
                        }}
                        onClick={handleSkip}
                    />
                    {/* Right overlay */}
                    <Box
                        style={{
                            position: "fixed",
                            top: targetElement.top,
                            left: targetElement.left + targetElement.width,
                            right: 0,
                            height: targetElement.height,
                            background: "rgba(0, 0, 0, 0.5)",
                            zIndex: 999,
                            transition: "all 0.3s ease",
                            // animation: `${fadeIn} 0.3s ease`,
                        }}
                        onClick={handleSkip}
                    />
                    {/* Bottom overlay */}
                    <Box
                        style={{
                            position: "fixed",
                            top: targetElement.top + targetElement.height,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "rgba(0, 0, 0, 0.5)",
                            zIndex: 999,
                            transition: "all 0.3s ease",
                            // animation: `${fadeIn} 0.3s ease`,
                        }}
                        onClick={handleSkip}
                    />

                    <PulsedBox
                        top={targetElement.top - 4}
                        left={targetElement.left - 4}
                        width={targetElement.width + 8}
                        height={targetElement.height + 8}
                    />
                </>
            )}

            {/* Tooltip - only visible when isTooltipVisible is true */}
            {isTargetFound && targetElement && isTooltipVisible && (
                <Paper
                    shadow="md"
                    p="md"
                    withBorder
                    style={{
                        position: "fixed",
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        width: 320,
                        zIndex: 1000,
                        backgroundColor: "white",
                        transition: "all 0.3s ease",
                        // animation: `${fadeIn} 0.3s ease`,
                    }}
                >
                    <Title order={4} c="#641E8C">
                        {step.title}
                    </Title>
                    <Text size="sm" mt="xs" style={{ whiteSpace: "pre-line" }}>
                        {step.description}
                    </Text>
                    <Group justify="space-between" mt="lg">
                        <Button
                            variant="subtle"
                            data-testid="skip-tour"
                            onClick={handleSkip}
                            style={{
                                color: "#641E8C",
                                "&:hover": { backgroundColor: "rgba(100, 30, 140, 0.1)" },
                            }}
                        >
                            Skip Tour
                        </Button>
                        <Button
                            onClick={handleNext}
                            style={{
                                backgroundColor: "#641E8C",
                                "&:hover": { backgroundColor: "#4F1971" },
                            }}
                        >
                            {currentStep < steps.length - 1 ? "Next" : "Finish"}
                        </Button>
                    </Group>
                    <Text size="xs" c="dimmed" ta="center" mt={8}>
                        Step {currentStep + 1} of {steps.length}
                    </Text>
                </Paper>
            )}
        </Portal>
    );
}
